import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { loginSchema, type LoginValues } from '@sf/shared';
import { Button } from '../../components/ui/Button';
import { PasswordField, TextField } from '../../components/ui/Field';
import { t } from '../../lib/copy';
import { messageForError } from '../../lib/errors';
import { login } from './api';

export function LoginPage() {
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    resetField,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({ resolver: zodResolver(loginSchema), mode: 'onTouched' });

  const onSubmit = handleSubmit(async ({ email, password }) => {
    setFormError(null);
    try {
      await login(email, password);
      // PublicOnly redirects once the profile loads.
    } catch (err) {
      setFormError(messageForError(err));
      resetField('password');
    }
  });

  return (
    <div>
      <h1 className="text-2xl font-bold">{t('auth.login.title')}</h1>
      <p className="mt-1 text-sm text-slate">Sign in to see your tickets and messages.</p>
      <form onSubmit={onSubmit} noValidate className="mt-6 flex flex-col gap-4">
        {formError && (
          <div
            role="alert"
            className="rounded-input border border-danger-700/30 bg-danger-50 px-3 py-2 text-sm text-danger-700"
          >
            {formError}
          </div>
        )}
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          required
          error={errors.email?.message}
          {...register('email')}
        />
        <PasswordField
          label="Password"
          autoComplete="current-password"
          required
          error={errors.password?.message}
          {...register('password')}
        />
        <div className="-mt-1 text-right">
          <Link
            to="/forgot-password"
            className="text-sm font-medium text-brand underline-offset-2 hover:underline"
          >
            Forgot password?
          </Link>
        </div>
        <Button type="submit" loading={isSubmitting} className="w-full justify-center">
          Sign in
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-slate">
        New here?{' '}
        <Link to="/signup" className="font-medium text-brand underline-offset-2 hover:underline">
          Create an account
        </Link>
      </p>
    </div>
  );
}
